// src/pages/Services.jsx
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FrontHeader from '../components/common/FrontHeader';
import FrontFooter from '../components/common/FrontFooter';
import heroBg from '../assets/image-7.jpeg'; 
import './Services.css';

const Services = () => {
  const navigate = useNavigate();
  const observerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('visible');
      });
    }, { threshold: 0.05 });

    document.querySelectorAll('.reveal').forEach(el => observerRef.current.observe(el));
    return () => observerRef.current?.disconnect();
  }, []);

  const handlePostRFQ = () => {
    const token = localStorage.getItem('marilink_token');
    if (token) navigate('/owner/post-job');
    else navigate('/login', { state: { from: '/owner/post-job' } });
  };

  const categories = [
    { icon: 'fa-anchor', name: 'Core Services', desc: 'Comprehensive basic maritime operations and tactical support for every vessel class.', count: '120+' },
    { icon: 'fa-building', name: 'Port Agency Services', desc: 'Expert local representation, customs clearance, and efficient port call management.', count: '85+' },
    { icon: 'fa-concierge-bell', name: 'Ship Chandling', desc: 'Full range delivery of deck, engine, and cabin stores alongside fresh provisions.', count: '200+' },
    { icon: 'fa-gas-pump', name: 'Bunkering Coordination', desc: 'Strategic fuel supply management and coordination for optimal efficiency.', count: '40+' },
    { icon: 'fa-microchip', name: 'Spare Parts Supply', desc: 'Procurement of genuine OEM and high-quality replacement parts worldwide.', count: '95+' },
    { icon: 'fa-oil-can', name: 'Marine Lubricants', desc: 'Supply of specialized lubricants and chemical solutions for marine engines.', count: '30+' },
    { icon: 'fa-tools', name: 'Technical Maintenance', desc: 'Scheduled maintenance and emergency repair services for all ship systems.', count: '150+' },
    { icon: 'fa-shield-alt', name: 'Safety Equipment Supply', desc: 'Procurement of Life Saving Appliances (LSA) and Fire Fighting Appliances (FFA).', count: '110+' },
    { icon: 'fa-clipboard-check', name: 'Class Inspection Support', desc: 'Professional assistance for class surveys ensuring full regulatory compliance.', count: '55+' },
    { icon: 'fa-swimmer', name: 'Hull Cleaning & Diving', desc: 'Underwater hull maintenance, propeller polishing, and diving inspections.', count: '25+' },
    { icon: 'fa-users', name: 'Crew Change Coordination', desc: 'Seamless logistics for crew embarkation, transport, and visa processing.', count: '70+' },
    { icon: 'fa-satellite-dish', name: 'GMDSS Services', desc: 'Annual radio surveys, battery load tests, and shore-based maintenance.', count: '45+' },
    { icon: 'fa-compass', name: 'Navigation Systems', desc: 'Calibration and repair of Radar, Gyro-compass, AIS, and ECDIS units.', count: '38+' },
    { icon: 'fa-satellite', name: 'VSAT & Comms', desc: 'Reliable satellite connectivity solutions and onboard network support.', count: '22+' },
    { icon: 'fa-bolt', name: 'Electrical Engineering', desc: 'Onboard electrical troubleshooting, automation, and power management.', count: '42+' },
    
    // FIXED ICONS BELOW
    { icon: 'fa-gears', name: 'Main Engine Overhaul', desc: 'Specialized 2-stroke and 4-stroke engine repairs and component reconditioning.', count: '15+' },
    { icon: 'fa-faucet-drip', name: 'Hydraulic Systems', desc: 'Service for steering gear, deck cranes, and hydraulic winches.', count: '20+' },
    
    { icon: 'fa-fan', name: 'HVAC & Refrigeration', desc: 'Repair and maintenance of marine AC systems and cold storage units.', count: '28+' },
    { icon: 'fa-battery-full', name: 'Battery Services', desc: 'Testing and replacement of emergency backup and radio batteries.', count: '18+' },
    { icon: 'fa-broadcast-tower', name: 'VDR / APT Testing', desc: 'Certified Annual Performance Tests (APT) for Voyage Data Recorders.', count: '19+' },
    { icon: 'fa-fire-extinguisher', name: 'Fire Fighting Service', desc: 'Inspection and refilling of extinguishers and fixed CO2 systems.', count: '65+' },
    { icon: 'fa-life-ring', name: 'Lifeboat & Davit Service', desc: 'Statutory 5-year and annual inspections for survival crafts.', count: '35+' },
    { icon: 'fa-tint', name: 'Ballast Water Solutions', desc: 'Installation and maintenance of environmental BWTS systems.', count: '12+' },
    { icon: 'fa-vial-circle-check', name: 'Lube Oil Analysis', desc: 'Laboratory analysis of machine oils to prevent equipment failure.', count: '10+' },
    { icon: 'fa-box-open', name: 'Logistics & Storage', desc: 'Bonded warehousing and secure transport for ship owner goods.', count: '45+' },
    { icon: 'fa-broom', name: 'Tank Cleaning', desc: 'Chemical and mechanical cleaning of fuel, water, and cargo tanks.', count: '14+' },
    { icon: 'fa-file-invoice-dollar', name: 'Customs Brokerage', desc: 'Authorized handling of shipping documents and spare parts clearance.', count: '50+' },
    { icon: 'fa-laptop-code', name: 'Marine IT Support', desc: 'Setup of cyber-secure networks and hardware maintenance onboard.', count: '15+' },
    { icon: 'fa-mask', name: 'Pest Control', desc: 'Certified fumigation and pest eradication for hygiene compliance.', count: '20+' },
    { icon: 'fa-ambulance', name: 'Medical & Healthcare', desc: 'Medical chest certification and supply of certified ship medicines.', count: '25+' }
  ];

  return (
    <div className="sv-page">
      <FrontHeader />

      {/* Hero Section */}
      <section className="sv-hero">
        <div className="sv-hero-bg" style={{ backgroundImage: `url(${heroBg})` }}></div>
        <div className="sv-hero-overlay"></div>
        <div className="sv-container-inner">
          <div className="sv-hero-content reveal">
            <span className="sv-section-tag">Direct • Secure • Verified</span>
            <h1 className="sv-hero-title">
              Complete <span className="sv-highlight">Vessel</span> Support
            </h1>
            <p className="sv-hero-subtitle">
              The premier destination for ship owners to connect with specialized service providers. 
              Access a comprehensive portfolio of maritime solutions across 500+ global ports.
            </p>
            <button className="sv-btn-main" onClick={handlePostRFQ}>
              Post a Service Request <i className="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="sv-categories">
        <div className="sv-container-inner">
          <div className="sv-section-header reveal">
            <span className="sv-section-tag">A to Z Services</span>
            <h2 className="sv-section-title">Explore Our Service Portfolio</h2>
          </div>
          
          <div className="sv-grid">
            {categories.map((cat, i) => (
              <div key={i} className="sv-card reveal" style={{ transitionDelay: `${(i % 4) * 50}ms` }}>
                <div className="sv-card-icon">
                  <i className={`fas ${cat.icon}`}></i>
                </div>
                <h3>{cat.name}</h3>
                <p>{cat.desc}</p>
                <div className="sv-card-footer">
                  <span className="sv-count">{cat.count} Providers</span>
                  <button onClick={handlePostRFQ} className="sv-card-btn">
                    Book Service <i className="fas fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="sv-bottom-cta">
        <div className="sv-container-inner">
           <div className="sv-cta-box reveal">
              <span className="sv-section-tag">Got a custom request?</span>
              <h2>Ready to Service Your Vessel?</h2>
              <p>
                Our network covers specialized engineers and reliable suppliers globally. 
                Post your job now and receive competitive quotes within hours.
              </p>
              <button className="sv-btn-main" onClick={handlePostRFQ}>
                Create an RFQ Now
              </button>
           </div>
        </div>
      </section>

      <FrontFooter />
    </div>
  );
};

export default Services;